/**
 * Manual Review — displays consumer-v1 quote fabrication audit results.
 *
 * Each row corresponds to one consumer article. The table summarises quote
 * verdicts as Strong / Weak / Low Match (with percentages). Expand a row to
 * see all attributed quotes, the source transcript link, KeyCrew search,
 * and the review controls (status / notes / fix-actions). Notes persist via
 * PATCH /api/audit/notes/{article_id}.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import auditService from '../../services/auditService';
import LoadingSpinner from '../common/LoadingSpinner';
import { BACKEND_URL } from '../../utils/constants';
import { authTokenParam } from '../../services/api';

// Internal verdict keys → user-facing labels
const VERDICT_LABEL = {
  EXACT: 'Strong',
  TIGHT_PARAPHRASE: 'Strong',
  PARAPHRASE: 'Strong',
  WEAK: 'Weak',
  FABRICATED: 'Low Match',
  TOO_SHORT: 'Too short',
};

// Quote-row chip colours
const VERDICT_COLOR = {
  EXACT: 'bg-green-100 text-green-800',
  TIGHT_PARAPHRASE: 'bg-green-100 text-green-800',
  PARAPHRASE: 'bg-green-100 text-green-800',
  WEAK: 'bg-yellow-100 text-yellow-800',
  FABRICATED: 'bg-red-100 text-red-800',
  TOO_SHORT: 'bg-gray-100 text-gray-700',
};

const STATUS_OPTIONS = [
  { value: 'needs_review', label: 'Needs Review', cls: 'bg-amber-100 text-amber-800' },
  { value: 'reviewed', label: 'Confirmed Error – Needs Fix', cls: 'bg-orange-100 text-orange-800' },
  { value: 'fixed', label: 'Fixed', cls: 'bg-green-100 text-green-800' },
  { value: 'dismissed', label: 'Dismissed (false positive)', cls: 'bg-gray-100 text-gray-700' },
];

const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'fab', label: 'Has Low Match' },
  { value: 'weak_or_fab', label: 'Has Weak or Low Match' },
  { value: 'needs_review', label: 'Needs Review' },
  { value: 'reviewed', label: 'Confirmed Error' },
  { value: 'fixed', label: 'Fixed' },
];

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
};

const StatusPill = ({ status }) => {
  const opt = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${opt.cls}`} data-testid={`status-pill-${status}`}>
      {opt.label}
    </span>
  );
};

/**
 * CountWithPct — vertical stack of count and percentage with verdict-tinted bg.
 *  tone: 'green' | 'yellow' | 'red'
 */
const CountWithPct = ({ count, total, tone, testId }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const cls =
    tone === 'red'
      ? 'bg-red-50 text-red-800 border-red-200'
      : tone === 'yellow'
      ? 'bg-yellow-50 text-yellow-800 border-yellow-200'
      : 'bg-green-50 text-green-800 border-green-200';
  if (!count) {
    return (
      <div className="inline-flex flex-col items-center text-gray-300 px-2" data-testid={testId}>
        <span className="text-sm font-semibold">0</span>
        <span className="text-[10px]">—</span>
      </div>
    );
  }
  return (
    <div className={`inline-flex flex-col items-center px-2 py-0.5 rounded border ${cls}`} data-testid={testId}>
      <span className="text-sm font-semibold leading-none">{count}</span>
      <span className="text-[10px] opacity-80 mt-0.5">{pct}%</span>
    </div>
  );
};

// Effective verdict bucket: collapses internal verdicts into 3 buckets used
// by the UI (Strong / Weak / Low Match). Honors a manual override when set.
const VERDICT_BUCKETS = { STRONG: 'STRONG', WEAK: 'WEAK', LOW_MATCH: 'LOW_MATCH' };

const algoBucket = (verdict) => {
  if (verdict === 'FABRICATED') return VERDICT_BUCKETS.LOW_MATCH;
  if (verdict === 'WEAK') return VERDICT_BUCKETS.WEAK;
  if (verdict === 'EXACT' || verdict === 'TIGHT_PARAPHRASE' || verdict === 'PARAPHRASE') return VERDICT_BUCKETS.STRONG;
  return null;
};

// Lightweight stable hash for quote text (so an override can be invalidated
// if the audit re-runs and the quote text at index i changed).
const quoteHash = (text) => {
  if (!text) return '';
  const norm = text.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().slice(0, 80);
  let h = 0;
  for (let i = 0; i < norm.length; i++) h = ((h << 5) - h + norm.charCodeAt(i)) | 0;
  return String(h);
};

const effectiveBucketFor = (q, idx, overrides) => {
  const o = overrides?.[String(idx)];
  if (o && o.verdict && (!o.text_hash || o.text_hash === quoteHash(q.quote))) {
    return o.verdict;
  }
  return algoBucket(q.verdict);
};

const BUCKET_COLOR = {
  STRONG: 'bg-green-100 text-green-800 border-green-300',
  WEAK: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  LOW_MATCH: 'bg-red-100 text-red-800 border-red-300',
};
const BUCKET_LABEL = { STRONG: 'Strong', WEAK: 'Weak', LOW_MATCH: 'Low Match' };

const VerdictPills = ({ activeBucket, isOverridden, onSelect, onReset, disabled }) => {
  const order = [VERDICT_BUCKETS.STRONG, VERDICT_BUCKETS.WEAK, VERDICT_BUCKETS.LOW_MATCH];
  return (
    <div className="inline-flex items-center gap-2">
      <div className="inline-flex rounded-md overflow-hidden border border-gray-200 bg-white" role="group">
        {order.map((b, i) => {
          const active = b === activeBucket;
          const cls = active
            ? BUCKET_COLOR[b]
            : 'text-gray-500 hover:bg-gray-50';
          const sep = i > 0 ? 'border-l border-gray-200' : '';
          return (
            <button
              key={b}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(b)}
              className={`px-2.5 py-1 text-xs font-medium ${cls} ${sep} disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid={`verdict-pill-${b.toLowerCase()}`}
            >
              {BUCKET_LABEL[b]}
            </button>
          );
        })}
      </div>
      {isOverridden && (
        <button
          type="button"
          onClick={onReset}
          className="text-[11px] text-blue-600 hover:underline"
          title="Reset to algorithm verdict"
          data-testid="verdict-reset"
        >
          ↺ algo
        </button>
      )}
      {isOverridden && (
        <span className="text-[10px] text-gray-500 italic" data-testid="verdict-manual-tag">manual</span>
      )}
    </div>
  );
};

const QuoteRow = ({ q, idx, overrides, onOverride }) => {
  const det = q.details || {};
  const algo = algoBucket(q.verdict);
  const effective = effectiveBucketFor(q, idx, overrides) || algo;
  const isOverridden = !!overrides?.[String(idx)];

  const isLow = effective === VERDICT_BUCKETS.LOW_MATCH;
  const isWeak = effective === VERDICT_BUCKETS.WEAK;
  const borderCls = isLow
    ? 'border-red-300 bg-red-50'
    : isWeak
    ? 'border-yellow-300 bg-yellow-50'
    : 'border-green-200 bg-green-50/40';

  return (
    <div className={`p-3 rounded border ${borderCls}`} data-testid={`quote-row-${idx}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <VerdictPills
          activeBucket={effective}
          isOverridden={isOverridden}
          onSelect={(b) => onOverride(idx, b, q.quote)}
          onReset={() => onOverride(idx, null, q.quote)}
        />
        <div className="flex gap-3 text-xs text-gray-600 whitespace-nowrap">
          <span>coverage: <span className="font-mono">{det.content_token_coverage_pct ?? 0}%</span></span>
          <span>longest match: <span className="font-mono">{det.longest_phrase_match_words ?? 0}w</span></span>
          {isOverridden && (
            <span className="text-gray-400">algo: <span className="font-mono">{BUCKET_LABEL[algo] || '—'}</span></span>
          )}
        </div>
      </div>
      <div className={`mt-2 text-sm leading-relaxed ${isLow ? 'text-red-900' : 'text-gray-800'}`}>
        “{q.quote}”
      </div>
    </div>
  );
};

/**
 * KeyCrewSearch — lightweight client-side search of the public WP REST API
 * for a matching post. Renders top 3 hits as clickable links; if none, shows
 * a manual-search fallback.
 */
const KeyCrewSearch = ({ title }) => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!title) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    setResults(null);
    // WP search works best with a short, distinctive phrase. The full title
    // often misses (em-dashes, colons, encoded chars). Use the first ~6 words
    // before the first dash/colon as a more forgiving query.
    const cleaned = title.replace(/[\u2013\u2014:|]/g, ' ').replace(/\s+/g, ' ').trim();
    const queryWords = cleaned.split(/\s+/).slice(0, 7).join(' ');
    const q = encodeURIComponent(queryWords);
    fetch(`https://keycrew.co/wp-json/wp/v2/posts?search=${q}&per_page=5&_fields=id,title,link,date`)
      .then((r) => {
        if (!r.ok) throw new Error(`WP API ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (!cancelled) setResults(Array.isArray(data) ? data : []);
      })
      .catch((e) => {
        if (!cancelled) setErr(e?.message || 'WP search failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [title]);

  const fallback = `https://keycrew.co/?s=${encodeURIComponent(title || '')}`;

  return (
    <div data-testid="keycrew-search">
      <h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">KeyCrew Article</h5>
      {loading && <div className="text-xs text-gray-500">Searching keycrew.co…</div>}
      {err && <div className="text-xs text-red-600">{err}</div>}
      {!loading && !err && results !== null && results.length === 0 && (
        <div className="text-xs text-gray-500">
          No automatic match.{' '}
          <a href={fallback} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline" data-testid="keycrew-fallback-link">
            Search manually ↗
          </a>
        </div>
      )}
      {!loading && results && results.length > 0 && (
        <ul className="space-y-1">
          {results.map((p) => (
            <li key={p.id} className="text-xs">
              <a
                href={p.link}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:underline break-all"
                data-testid={`keycrew-result-${p.id}`}
              >
                {p.title?.rendered || p.link} ↗
              </a>
              <span className="text-gray-400 ml-2">{formatDate(p.date)}</span>
            </li>
          ))}
          <li className="text-[11px]">
            <a href={fallback} target="_blank" rel="noreferrer" className="text-gray-500 hover:underline">
              More on keycrew.co ↗
            </a>
          </li>
        </ul>
      )}
    </div>
  );
};

const RowDetail = ({ row, onSave, onOverride, isSaving, savedAt, errorMsg }) => {
  const [notes, setNotes] = useState(row.notes || '');
  const [status, setStatus] = useState(row.review_status || 'needs_review');
  const [fixActions, setFixActions] = useState(row.fix_actions || '');

  useEffect(() => {
    setNotes(row.notes || '');
    setStatus(row.review_status || 'needs_review');
    setFixActions(row.fix_actions || '');
  }, [row.article_id, row.notes, row.review_status, row.fix_actions]);

  const dirty =
    (notes || '') !== (row.notes || '') ||
    (status || 'needs_review') !== (row.review_status || 'needs_review') ||
    (fixActions || '') !== (row.fix_actions || '');

  const transcriptUrl = row.source_interview_id
    ? `${BACKEND_URL}/api/audit/transcript/${row.source_interview_id}?${authTokenParam()}`
    : null;

  return (
    <td colSpan={9} className="bg-gray-50 px-6 py-4 border-t border-gray-200">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: quotes */}
        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h4 className="text-sm font-semibold text-gray-900">
              Quotes ({row.total_quotes}) — Transcript: {row.transcript_chars.toLocaleString()} chars
            </h4>
            <div className="flex gap-3 text-xs">
              {transcriptUrl && (
                <a
                  href={transcriptUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 hover:underline"
                  data-testid={`open-transcript-link-${row.article_id}`}
                >
                  Open transcript ↗
                </a>
              )}
            </div>
          </div>
          {(row.quotes || []).map((q, i) => (
            <QuoteRow
              key={i}
              q={q}
              idx={i}
              overrides={row.quote_overrides}
              onOverride={onOverride}
            />
          ))}
        </div>

        {/* Right: review controls + KeyCrew lookup */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-900">Review</h4>

          <label className="block">
            <span className="text-xs font-medium text-gray-700">Status</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="mt-1 w-full text-sm rounded border border-gray-300 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid={`status-select-${row.article_id}`}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-xs font-medium text-gray-700">Notes</span>
            <textarea
              rows={5}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes about flagged quotes, context, decisions…"
              className="mt-1 w-full text-sm rounded border border-gray-300 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid={`notes-input-${row.article_id}`}
            />
          </label>

          <label className="block">
            <span className="text-xs font-medium text-gray-700">Fix actions taken</span>
            <textarea
              rows={3}
              value={fixActions}
              onChange={(e) => setFixActions(e.target.value)}
              placeholder='e.g. "Quote 3 deleted", "Article unpublished", "Sent to KeyCrew"…'
              className="mt-1 w-full text-sm rounded border border-gray-300 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid={`fix-actions-input-${row.article_id}`}
            />
          </label>

          <div className="flex items-center gap-3">
            <button
              disabled={!dirty || isSaving}
              onClick={() => onSave({ notes, review_status: status, fix_actions: fixActions })}
              className={`px-3 py-1.5 rounded text-sm font-medium ${dirty && !isSaving ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}
              data-testid={`save-review-btn-${row.article_id}`}
            >
              {isSaving ? 'Saving…' : dirty ? 'Save' : 'Saved'}
            </button>
            {errorMsg && (
              <span className="text-xs text-red-600" data-testid={`save-error-${row.article_id}`}>
                {errorMsg}
              </span>
            )}
            {!errorMsg && savedAt && (
              <span className="text-xs text-gray-500" data-testid={`save-stamp-${row.article_id}`}>
                Saved {formatDate(savedAt)}
              </span>
            )}
          </div>

          <div className="border-t border-gray-200 pt-3">
            <KeyCrewSearch title={row.title} />
          </div>

          <div className="text-xs text-gray-400 border-t border-gray-200 pt-3">
            <div>Article ID: <span className="font-mono break-all">{row.article_id}</span></div>
            <div>Generated: {formatDate(row.generated_at)}</div>
            <div>Model: <span className="font-mono">{row.model}</span></div>
          </div>
        </div>
      </div>
    </td>
  );
};

const ManualReviewView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('weak_or_fab');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [errorById, setErrorById] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await auditService.getResults();
      setData(r);
    } catch (e) {
      setError(e?.message || 'Failed to load audit results');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (articleId, payload) => {
    setSavingId(articleId);
    setErrorById((p) => ({ ...p, [articleId]: null }));
    try {
      const r = await auditService.updateNotes(articleId, payload);
      const saved = r?.data || {};
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          results: prev.results.map((row) =>
            row.article_id === articleId
              ? {
                  ...row,
                  notes: saved.notes ?? payload.notes ?? row.notes,
                  review_status: saved.review_status ?? payload.review_status ?? row.review_status,
                  fix_actions: saved.fix_actions ?? payload.fix_actions ?? row.fix_actions,
                  notes_updated_at: saved.updated_at || new Date().toISOString(),
                }
              : row
          ),
        };
      });
    } catch (e) {
      setErrorById((p) => ({ ...p, [articleId]: e?.message || 'Save failed' }));
    } finally {
      setSavingId(null);
    }
  };

  // Per-quote manual override. bucket=null clears the override.
  const handleOverride = async (articleId, quoteIdx, bucket, quoteText) => {
    // Optimistic update
    let nextOverrides = null;
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        results: prev.results.map((row) => {
          if (row.article_id !== articleId) return row;
          const cur = { ...(row.quote_overrides || {}) };
          if (bucket) {
            cur[String(quoteIdx)] = {
              verdict: bucket,
              text_hash: quoteHash(quoteText),
              set_at: new Date().toISOString(),
            };
          } else {
            delete cur[String(quoteIdx)];
          }
          nextOverrides = cur;
          return { ...row, quote_overrides: cur };
        }),
      };
    });
    try {
      await auditService.updateNotes(articleId, { quote_overrides: nextOverrides || {} });
    } catch (e) {
      setErrorById((p) => ({ ...p, [articleId]: e?.message || 'Override save failed' }));
      // Revert on failure
      load();
    }
  };

  // Effective bucket counts for a row (honors manual overrides).
  const effectiveCounts = useCallback((r) => {
    const overrides = r.quote_overrides || {};
    let strong = 0, weak = 0, low = 0;
    (r.quotes || []).forEach((q, i) => {
      const b = effectiveBucketFor(q, i, overrides);
      if (b === VERDICT_BUCKETS.STRONG) strong += 1;
      else if (b === VERDICT_BUCKETS.WEAK) weak += 1;
      else if (b === VERDICT_BUCKETS.LOW_MATCH) low += 1;
    });
    return { strong, weak, low };
  }, []);

  const rows = useMemo(() => {
    if (!data?.results) return [];
    let out = data.results.filter((r) => !r.error).map((r) => ({ ...r, _eff: effectiveCounts(r) }));
    if (filter === 'fab') out = out.filter((r) => r._eff.low > 0);
    else if (filter === 'weak_or_fab') out = out.filter((r) => r._eff.low + r._eff.weak > 0);
    else if (['needs_review', 'reviewed', 'fixed', 'dismissed'].includes(filter)) {
      out = out.filter((r) => (r.review_status || 'needs_review') === filter);
    }
    if (search.trim()) {
      const s = search.toLowerCase();
      out = out.filter(
        (r) =>
          (r.interviewee || '').toLowerCase().includes(s) ||
          (r.title || '').toLowerCase().includes(s) ||
          (r.company || '').toLowerCase().includes(s)
      );
    }
    out.sort((a, b) => {
      const fa = b._eff.low - a._eff.low;
      if (fa) return fa;
      return b._eff.weak - a._eff.weak;
    });
    return out;
  }, [data, filter, search, effectiveCounts]);

  const stats = useMemo(() => {
    if (!data?.results) return { articles: 0, fab: 0, weak: 0, strong: 0, fabArticles: 0, totalQuotes: 0 };
    let fab = 0, weak = 0, strong = 0, fabArticles = 0, totalQuotes = 0;
    for (const r of data.results) {
      if (r.error) continue;
      const e = effectiveCounts(r);
      strong += e.strong;
      weak += e.weak;
      fab += e.low;
      totalQuotes += r.total_quotes || 0;
      if (e.low > 0) fabArticles += 1;
    }
    return { articles: data.results.length, fab, weak, strong, fabArticles, totalQuotes };
  }, [data, effectiveCounts]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading audit results…" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6 text-red-700" data-testid="manual-review-load-error">
        Failed to load audit results: {error}
        <button onClick={load} className="ml-3 px-3 py-1 bg-red-600 text-white rounded text-sm">Retry</button>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="manual-review-view">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manual Review — Quote Audit</h1>
          <p className="mt-1 text-sm text-gray-600">
            Audit of <span className="font-semibold">{data?.model_audited}</span> consumer articles.
            Algorithm version: <span className="font-mono">{data?.audit_version}</span>.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href={`${BACKEND_URL}/api/audit/export/flagged-quotes.csv?scope=flagged&${authTokenParam()}`}
            download
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
            data-testid="export-csv-btn"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Export Weak + Low Match
          </a>
          <a
            href={`${BACKEND_URL}/api/audit/export/flagged-quotes.csv?scope=all&${authTokenParam()}`}
            download
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
            data-testid="export-all-csv-btn"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Export All Quotes
          </a>
          <a
            href={`${BACKEND_URL}/api/audit/export/transcripts.csv?${authTokenParam()}`}
            download
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
            data-testid="export-transcripts-btn"
            title="Full interview list with transcripts + source metadata (~45 MB, ~10s)"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Export All Transcripts
          </a>
          <a
            href={`${BACKEND_URL}/api/audit/export/full-joined.csv?${authTokenParam()}`}
            download
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white border border-blue-700 rounded hover:bg-blue-700"
            data-testid="export-full-joined-btn"
            title="One row per quote with article + interview + transcript inline (~120 MB, ~15s)"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Export Full (Joined)
          </a>
        </div>
      </div>

      {/* Stats cards — mirror the table column framing */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <StatCard label="Articles audited" value={stats.articles} sub={`${stats.fabArticles} w/ Low Match`} subTone="red" />
        <StatCard label="Total quotes" value={stats.totalQuotes} />
        <StatCard
          label="Strong"
          tone="green"
          value={stats.strong}
          sub={stats.totalQuotes ? `${Math.round((stats.strong / stats.totalQuotes) * 100)}%` : ''}
        />
        <StatCard
          label="Weak"
          tone="yellow"
          value={stats.weak}
          sub={stats.totalQuotes ? `${Math.round((stats.weak / stats.totalQuotes) * 100)}%` : ''}
        />
        <StatCard
          label="Low Match"
          tone="red"
          value={stats.fab}
          sub={stats.totalQuotes ? `${Math.round((stats.fab / stats.totalQuotes) * 100)}%` : ''}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="text-sm border border-gray-300 rounded px-2 py-1.5 bg-white"
          data-testid="filter-select"
        >
          {FILTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search interviewee / title / company"
          className="text-sm border border-gray-300 rounded px-3 py-1.5 w-72"
          data-testid="search-input"
        />
        <button onClick={load} className="text-sm bg-white border border-gray-300 px-3 py-1.5 rounded hover:bg-gray-50" data-testid="refresh-btn">
          Refresh
        </button>
        <span className="text-xs text-gray-500 ml-auto">Showing {rows.length} of {stats.articles}</span>
      </div>

      {/* Table */}
      <div className="bg-white shadow border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="audit-table">
          <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left font-medium w-8"></th>
              <th className="px-3 py-2 text-left font-medium">Interviewee</th>
              <th className="px-3 py-2 text-left font-medium">Article</th>
              <th className="px-3 py-2 text-right font-medium">Quotes</th>
              <th className="px-3 py-2 text-center font-medium text-green-700">Strong</th>
              <th className="px-3 py-2 text-center font-medium text-yellow-700">Weak</th>
              <th className="px-3 py-2 text-center font-medium text-red-700">Low Match</th>
              <th className="px-3 py-2 text-left font-medium">Status</th>
              <th className="px-3 py-2 text-left font-medium">Notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isExpanded = expanded === r.article_id;
              const eff = r._eff || { strong: 0, weak: 0, low: 0 };
              return (
                <React.Fragment key={r.article_id}>
                  <tr
                    className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${isExpanded ? 'bg-blue-50' : ''}`}
                    onClick={() => setExpanded(isExpanded ? null : r.article_id)}
                    data-testid={`audit-row-${r.article_id}`}
                  >
                    <td className="px-3 py-2 text-gray-400">{isExpanded ? '▾' : '▸'}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">{r.interviewee}</div>
                      <div className="text-xs text-gray-500 truncate max-w-[14rem]">{r.company}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-gray-800 truncate max-w-[28rem]" title={r.title}>{r.title}</div>
                      <div className="text-xs text-gray-400">{formatDate(r.generated_at)}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{r.total_quotes}</td>
                    <td className="px-3 py-2 text-center">
                      <CountWithPct count={eff.strong} total={r.total_quotes} tone="green" testId={`strong-cell-${r.article_id}`} />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <CountWithPct count={eff.weak} total={r.total_quotes} tone="yellow" testId={`weak-cell-${r.article_id}`} />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <CountWithPct count={eff.low} total={r.total_quotes} tone="red" testId={`lowmatch-cell-${r.article_id}`} />
                    </td>
                    <td className="px-3 py-2"><StatusPill status={r.review_status || 'needs_review'} /></td>
                    <td className="px-3 py-2 text-xs text-gray-600 max-w-[14rem] truncate" title={r.notes || ''}>
                      {r.notes || <span className="text-gray-400">—</span>}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <RowDetail
                        row={r}
                        onSave={(payload) => handleSave(r.article_id, payload)}
                        onOverride={(idx, bucket, qtext) => handleOverride(r.article_id, idx, bucket, qtext)}
                        isSaving={savingId === r.article_id}
                        savedAt={r.notes_updated_at}
                        errorMsg={errorById[r.article_id]}
                      />
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={9} className="px-6 py-12 text-center text-gray-500" data-testid="empty-state">
                  No articles match the current filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, accent, tone, sub, subTone }) => {
  // tone applies the same green/yellow/red framing used for cells.
  const t = tone || accent;
  const cardCls =
    t === 'red'
      ? 'bg-red-50 border-red-200'
      : t === 'yellow'
      ? 'bg-yellow-50 border-yellow-200'
      : t === 'green'
      ? 'bg-green-50 border-green-200'
      : 'bg-white border-gray-200';
  const labelCls =
    t === 'red'
      ? 'text-red-700'
      : t === 'yellow'
      ? 'text-yellow-800'
      : t === 'green'
      ? 'text-green-700'
      : 'text-gray-500';
  const valueCls =
    t === 'red'
      ? 'text-red-700'
      : t === 'yellow'
      ? 'text-yellow-800'
      : t === 'green'
      ? 'text-green-700'
      : 'text-gray-900';
  const subCls =
    subTone === 'red'
      ? 'text-red-600'
      : subTone === 'yellow'
      ? 'text-yellow-700'
      : subTone === 'green'
      ? 'text-green-700'
      : 'text-gray-500';
  return (
    <div className={`border rounded-lg p-3 ${cardCls}`}>
      <div className={`text-xs uppercase tracking-wide ${labelCls}`}>{label}</div>
      <div className={`mt-1 text-2xl font-semibold leading-tight ${valueCls}`}>{value}</div>
      {sub && <div className={`text-xs mt-0.5 ${subCls}`}>{sub}</div>}
    </div>
  );
};

export default ManualReviewView;
